import { memo, useCallback, useMemo } from "react";

import type { DayOfTheWeek, InputMaybe } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForArrayType, ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import { FormErrorMessage, FormItem } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxGroupField } from "@/components/ui/form/fields/check-box-field/check-box-group";


import {
	HOLIDAY_SALES_RADIO_OPTIONS,
	HOLIDAY_SALES_SECTION_DESCRIPTION,
	HOLIDAY_SALES_SECTION_TITLE,
	HOLIDAY_STAY_RADIO_OPTIONS,
	HOLIDAY_STAY_SECTION_DESCRIPTION,
	HOLIDAY_STAY_SECTION_TITLE,
} from "./holiday-radio-labels";
import { HolidayStayableRadioSubBlock } from "./holiday-stayable-radio-sub-block";

import type { CheckedState } from "@radix-ui/react-checkbox";

type HolidayCopyVariant = "stay" | "sales";

type Props = {
	stayableDayOfTheWeek: ForArrayType<InputMaybe<DayOfTheWeek[]> | undefined> | undefined;
	rawStayableDayOfTheWeek: ForAutoGenerator<DayOfTheWeek, "">[];
	onSetStayableDayOfTheWeek: (input: CheckedState, item: FormOptionType) => void;
	stayableDayOfTheWeekOptions: FormOptionType[];

	guide?: string;
	title?: string;

	// 祝日設定（3状態: undefined=未設定, true=含める, false=含めない）
	isHolidayStayable?: boolean | null;
	onSetIsHolidayStayable?: (value: boolean | undefined) => void;

	/** 祝日ラジオの文言コンテキスト（宿泊プラン vs オプション販売） */
	holidayCopyVariant?: HolidayCopyVariant;
};

export const QuestionStayableDayOfTheWeek = memo(
	({
		stayableDayOfTheWeek,
		rawStayableDayOfTheWeek,
		onSetStayableDayOfTheWeek,
		stayableDayOfTheWeekOptions,

		title: _title,
		guide: _guide,

		isHolidayStayable,
		onSetIsHolidayStayable,
		holidayCopyVariant = "stay",
	}: Props) => {
		const title = _title || "宿泊可能な曜日";
		const guide = _guide || "";

		const holidaySection =
			holidayCopyVariant === "sales"
				? {
						options: HOLIDAY_SALES_RADIO_OPTIONS,
						sectionTitle: HOLIDAY_SALES_SECTION_TITLE,
						sectionDescription: HOLIDAY_SALES_SECTION_DESCRIPTION,
					}
				: {
						options: HOLIDAY_STAY_RADIO_OPTIONS,
						sectionTitle: HOLIDAY_STAY_SECTION_TITLE,
						sectionDescription: HOLIDAY_STAY_SECTION_DESCRIPTION,
					};

		const holidayValue = useMemo(() => {
			if (isHolidayStayable === true) return "true";
			if (isHolidayStayable === false) return "false";
			return "unset";
		}, [isHolidayStayable]);

		const handleHolidayChange = useCallback(
			(value: string) => {
				if (!onSetIsHolidayStayable) return;
				if (value === "true") onSetIsHolidayStayable(true);
				else if (value === "false") onSetIsHolidayStayable(false);
				else onSetIsHolidayStayable(undefined);
			},
			[onSetIsHolidayStayable],
		);

		return (
			<FeatureIslandCategoryQuestionForMain
				required={false}
				title={title}
				guide={guide}
				hasError={stayableDayOfTheWeek?.hasError ?? false}
				isEdited={stayableDayOfTheWeek?.isEdited ?? false}
				inputChildren={
					<FormItem>
						<CheckboxGroupField
							value={rawStayableDayOfTheWeek}
							options={stayableDayOfTheWeekOptions}
							handleCheckedChange={onSetStayableDayOfTheWeek}
							gridCols={2}
						/>
						<FormErrorMessage>{stayableDayOfTheWeek?.error}</FormErrorMessage>
						{onSetIsHolidayStayable && (
							<HolidayStayableRadioSubBlock
								sectionTitle={holidaySection.sectionTitle}
								sectionDescription={holidaySection.sectionDescription}
								options={holidaySection.options}
								value={holidayValue}
								onValueChange={handleHolidayChange}
							/>
						)}
					</FormItem>
				}
			/>
		);
	},
);

QuestionStayableDayOfTheWeek.displayName = "QuestionStayableDayOfTheWeek";
